import React, { useRef, useEffect, useContext } from 'react';
import * as THREE from 'three';

import { PalletConfigObj } from 'blackbox';
import { useScene } from './hooks/useScene';
import { PalletGroup } from './createPalletGroup';
import { useAssets } from '../../context/AssetsContext';
import { PMREMGenerator } from 'three';
import { useCoreAppConfig } from '../General';
import { Application } from 'libapi';
import { ApplicationContext } from '../../App';

interface OperationSceneProps {
	palletConfig: PalletConfigObj;

	/** Where we are in the left pallet’s items. If undefined => show all items. */
	leftPosition?: number;
	/** Where we are in the right pallet’s items. If undefined => show all items. */
	rightPosition?: number;

	/** Whether to highlight the last placed item on the left pallet. */
	leftUseHighlights?: boolean;
	/** Whether to highlight the last placed item on the right pallet. */
	rightUseHighlights?: boolean;

	leftStackIndex?: number;
	leftPalletIndex?: number;

	rightStackIndex?: number;
	rightPalletIndex?: number;

	recenter?: number;
}

export function OperationScene({
	palletConfig,
	leftPosition,
	rightPosition,
	leftUseHighlights = true,
	rightUseHighlights = true,
	leftStackIndex,
	leftPalletIndex,
	rightStackIndex,
	rightPalletIndex,
	recenter,
}: OperationSceneProps) {
	const mountRef = useRef<HTMLDivElement>(null);
	const assets = useAssets();
	const app = useContext(ApplicationContext) as Application;
	const coreAppConfig = useCoreAppConfig(app).coreAppConfig;
	const [robotVersion, setRobotVersion] = React.useState<string | null>(null);
	const { sceneRef, cameraRef, controlsRef, rendererRef, frameContent } =
		useScene({
			mountRef,
			minDistance: 1000,
			maxDistance: 5000,
		});

	useEffect(() => {
		if (!coreAppConfig || !coreAppConfig.System?.RSPV?.v) return;
		const rspvVersion = coreAppConfig.System.RSPV.v;
		setRobotVersion(rspvVersion);
	}, [coreAppConfig?.System?.RSPV?.v]);

	// ------------------------------------------------------------------
	// 1) Robot Model
	// ------------------------------------------------------------------

	useEffect(() => {
		if (
			!sceneRef.current ||
			!rendererRef.current ||
			!robotVersion ||
			!assets.hdrTexture
		)
			return;
		const robotModel =
			robotVersion === 'Version 1' ? assets.v1RobotScene : assets.v2RobotScene;

		if (!robotModel) {
			return;
		}

		// Remove both in case one was mounted before
		sceneRef.current.children
			.filter(obj => obj === assets.v1RobotScene || obj === assets.v2RobotScene)
			.forEach(obj => sceneRef.current?.remove(obj));

		// Mount robot model
		const pmremGenerator = new PMREMGenerator(rendererRef.current);
		sceneRef.current.background = null;
		sceneRef.current.add(robotModel);
		const envRT = pmremGenerator.fromEquirectangular(assets.hdrTexture);
		sceneRef.current.environment = envRT.texture;
		scaleScene();
		return () => {
			sceneRef.current?.remove(robotModel);
			envRT.dispose();
			pmremGenerator.dispose();
		};
	}, [sceneRef, rendererRef, robotVersion, assets.materialsReady]);

	const leftGroupRef = useRef<THREE.Group>(new THREE.Group());
	const rightGroupRef = useRef<THREE.Group>(new THREE.Group());

	// ------------------------------------------------------------------
	// 2) Create left & right groups for pallets, offset & scale them
	// ------------------------------------------------------------------
	useEffect(() => {
		if (!sceneRef.current) return;
		const isVersion1 = robotVersion === 'Version 1';
		const offsetY = isVersion1 ? 0 : 20;

		const leftGroup = leftGroupRef.current;
		const rightGroup = rightGroupRef.current;

		leftGroup.position.set(0, 0, 0);
		rightGroup.position.set(0, 0, 0);

		leftGroup.rotation.x = rightGroup.rotation.x = -Math.PI / 2;

		sceneRef.current.add(leftGroup);
		sceneRef.current.add(rightGroup);
		sceneRef.current.position.y += offsetY;
	}, [sceneRef, assets.materialsReady, robotVersion]);

	// ------------------------------------------------------------------
	// 3) Pull the camera back to capture everything in the scene
	// ------------------------------------------------------------------
	useEffect(() => {
		// Run your scene recenter logic when requested
		requestAnimationFrame(() => {
			if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;
			const box = new THREE.Box3().setFromObject(sceneRef.current);
			let size = new THREE.Vector3();
			box.getSize(size);
			let maxDim = Math.max(size.x, size.y, size.z);
			let height = box.max.y - box.min.y;
			if (robotVersion !== 'Version 1') {
				maxDim *= 1.5;
				height *= 1.5;
			}
			frameContent(maxDim, height);
			const requiredDistance =
				height /
				1.5 /
				Math.sin(THREE.MathUtils.degToRad(cameraRef.current!.fov) / 2);

			const dir = new THREE.Vector3()
				.subVectors(cameraRef.current!.position, controlsRef.current!.target)
				.normalize();
			const newPos = controlsRef
				.current!.target.clone()
				.add(dir.multiplyScalar(requiredDistance));
			cameraRef.current!.position.copy(newPos);
			cameraRef.current!.updateProjectionMatrix();
			controlsRef.current!.update();
		});
	}, [recenter, robotVersion, assets.materialsReady]);

	// ------------------------------------------------------------------
	// 4) Render the "headless" PalletGroup for left & right
	// ------------------------------------------------------------------
	function offsetPallet(side: 'left' | 'right', length: number, width: number) {
		// for rspv1 we just offset the pallet slightly, but in v2 we want to align it with the model's pallet locators
		const defaultOffsetX = robotVersion === 'Version 1' ? 0 : 520;
		const defaultOffsetZ = robotVersion === 'Version 1' ? -50 : 830;

		if (!sceneRef.current) return;
		const group = side === 'left' ? leftGroupRef.current : rightGroupRef.current;
		group.position.z =
			robotVersion === 'Version 1'
				? -defaultOffsetZ
				: -defaultOffsetZ + length / 2;
		if (side === 'left') {
			group.position.x =
				robotVersion === 'Version 1'
					? -defaultOffsetX - width
					: -defaultOffsetX - width / 2;
		} else {
			group.position.x =
				robotVersion === 'Version 1'
					? defaultOffsetX + width
					: defaultOffsetX + width / 2;
		}
	}

	function scaleScene(scale: number = 0.1) {
		sceneRef.current?.scale.setScalar(scale);
	}

	const hasLeftPallet =
		leftStackIndex !== undefined && leftPalletIndex !== undefined;
	const hasRightPallet =
		rightStackIndex !== undefined && rightPalletIndex !== undefined;

	return (
		<div
			ref={mountRef}
			style={{
				width: '100%',
				height: '100%',
				position: 'relative',
			}}
		>
			{/* LEFT pallet? */}
			{hasLeftPallet && sceneRef.current && (
				<PalletGroup
					parentScene={leftGroupRef.current}
					palletConfig={palletConfig}
					stackIndex={leftStackIndex}
					palletIndex={leftPalletIndex}
					bayIndex={0}
					position={leftPosition}
					useHighlights={leftUseHighlights}
					applyOffset={(length, width) => offsetPallet('left', length, width)}
					scaleScene={scaleScene}
				/>
			)}

			{/* RIGHT pallet? */}
			{hasRightPallet && sceneRef.current && (
				<PalletGroup
					parentScene={rightGroupRef.current}
					palletConfig={palletConfig}
					stackIndex={rightStackIndex}
					palletIndex={rightPalletIndex}
					bayIndex={1}
					position={rightPosition}
					useHighlights={rightUseHighlights}
					applyOffset={(length, width) => offsetPallet('right', length, width)}
					scaleScene={scaleScene}
				/>
			)}
		</div>
	);
}
